import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Archive, ArrowLeft, MoveRight, Snowflake, Sun, Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { POSITION_STATUS_LABELS, REQUISITION_STATUSES, type PositionStatus, type RequisitionStatus } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { SkeletonLines } from '../../../components/Skeleton';
import { REQUISITION_STATUS_TONES } from '../../../components/status-tones';
import {
  ForbiddenState,
  NotFoundState,
  ServerErrorState,
} from '../../../components/system-states/SystemState';
import type { Tone } from '../../../components/Tone';
import { EMPTY, formatDateTime, formatNumber, orEmpty } from '../../../lib/format';
import { errorMessage } from '../../../lib/api-client';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { FreezeDialog } from '../components/FreezeDialog';
import { PositionHistory } from '../components/PositionHistory';
import { PositionStatusChip } from '../components/PositionStatusChip';
import { ReassignDialog } from '../components/ReassignDialog';
import { SalaryBand } from '../components/SalaryBand';
import {
  useArchivePosition,
  useChangePositionStatus,
  usePosition,
  useRestorePosition,
  useUnfreezePosition,
  type LinkedRequisition,
} from '../api/organisation-api';
import styles from './PositionDetailPage.module.css';
import { goneId, useRecordVisit } from '../../../lib/use-record-visit';

/**
 * ORG-03 — one seat's whole record.
 *
 * E1: "one position's history: every occupant, every requisition ever raised
 * against it, budget info." Occupants arrive with S24; the other two are here.
 *
 * The history is read from the event log, not from a change table. That is what
 * makes 06-qol §12 true for free — "every override, waiver and skip is visible in
 * the record's history, not buried in an audit log nobody opens" — because there is
 * only one log and this is a view of it.
 */

/** Where a seat can go from here, minus the ones that need their own dialog. */
const SIMPLE_TARGETS: Readonly<Record<PositionStatus, readonly PositionStatus[]>> = {
  planned: ['vacant'],
  vacant: ['in_recruitment', 'planned', 'filled'],
  in_recruitment: ['vacant', 'filled'],
  filled: ['vacant'],
  becoming_vacant: ['vacant', 'filled'],
  frozen: [],
};

export function PositionDetailPage(): ReactNode {
  const canView = useCanView('org_unit_position');
  const canEdit = useCanEdit('org_unit_position');
  const navigate = useNavigate();
  const { positionId } = useParams({ from: '/app-layout/organisation/positions/$positionId' });
  const showToast = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);

  const position = usePosition(canView ? positionId : null);
  useRecordVisit(position.data ? { kind: 'position', id: positionId, title: position.data.title, subtitle: position.data.code, path: `/organisation/positions/${positionId}` } : null, goneId(position.error, positionId));
  const changeStatus = useChangePositionStatus();
  const unfreeze = useUnfreezePosition();
  const archive = useArchivePosition();
  const restore = useRestorePosition();

  const [isFreezing, setIsFreezing] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);

  if (!canView) return <ForbiddenState resource="this position" contact="Your admin" />;
  if (position.isError) {
    return position.error.status === 404 ? (
      <NotFoundState onGoBack={() => void navigate({ to: '/organisation/positions' })} />
    ) : (
      <ServerErrorState onRetry={() => void position.refetch()} />
    );
  }

  if (position.isPending || !position.data) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={6} />
      </div>
    );
  }

  const row = position.data;
  const isArchived = row.archivedAt !== null;
  const activeRequisition = row.requisitions.find((r) => r.isActive) ?? null;

  async function moveTo(status: PositionStatus): Promise<void> {
    try {
      await changeStatus.mutateAsync({ id: row.id, version: row.version, status });
      showToast({ tone: 'success', message: `${row.code} is now ${POSITION_STATUS_LABELS[status].toLowerCase()}` });
    } catch (cause) {
      showToast({ tone: 'danger', message: 'That change was not saved', detail: errorMessage(cause) });
    }
  }

  async function handleArchive(): Promise<void> {
    try {
      const archived = await archive.mutateAsync({ id: row.id, version: row.version });
      showUndo(`${row.code} archived`, () => {
        void restore.mutateAsync({ id: archived.id, version: archived.version });
      });
    } catch (cause) {
      showToast({ tone: 'danger', message: `${row.code} was not archived`, detail: errorMessage(cause) });
    }
  }

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/organisation" className={styles.crumb}>
          Organisation
        </Link>
        <span aria-hidden>/</span>
        <Link to="/organisation/positions" className={styles.crumb}>
          Positions
        </Link>
        <span aria-hidden>/</span>
        <span>{row.code}</span>
      </nav>

      <header className={styles.head}>
        <div className={styles.headText}>
          <span className={styles.code}>{row.code}</span>
          <h1 className={styles.title}>{row.title}</h1>
          <p className={styles.unit}>
            {row.ventureName} · {row.departmentName}
            {row.teamName !== null ? ` · ${row.teamName}` : ''}
          </p>
        </div>

        <div className={styles.headStatus}>
          <PositionStatusChip position={row} />
          {row.frozenReason !== null && <p className={styles.frozenReason}>{row.frozenReason}</p>}
        </div>
      </header>

      {canEdit && (
        <div className={styles.actions}>
          <Button variant="ghost" icon={ArrowLeft} onClick={() => void navigate({ to: '/organisation/positions' })}>
            Back to the ledger
          </Button>

          {isArchived ? (
            <Button
              variant="solid"
              icon={Undo2}
              isPending={restore.isPending}
              onClick={() => void restore.mutateAsync({ id: row.id, version: row.version })}
            >
              Restore
            </Button>
          ) : (
            <>
              {SIMPLE_TARGETS[row.status].map((target) => (
                <Button
                  key={target}
                  variant="outline"
                  isPending={changeStatus.isPending}
                  onClick={() => void moveTo(target)}
                >
                  Mark {POSITION_STATUS_LABELS[target].toLowerCase()}
                </Button>
              ))}

              {row.status === 'frozen' ? (
                <Button
                  variant="outline"
                  icon={Sun}
                  isPending={unfreeze.isPending}
                  onClick={() => void unfreeze.mutateAsync({ id: row.id, version: row.version })}
                >
                  Unfreeze
                </Button>
              ) : (
                <Button variant="outline" icon={Snowflake} onClick={() => setIsFreezing(true)}>
                  Freeze
                </Button>
              )}

              <Button variant="outline" icon={MoveRight} onClick={() => setIsReassigning(true)}>
                Move
              </Button>

              <Button variant="outline" icon={Archive} onClick={() => void handleArchive()}>
                Archive
              </Button>
            </>
          )}
        </div>
      )}

      <div className={styles.grid}>
        <Card title="The seat">
          <dl className={styles.facts}>
            <Fact label="Employment type" value={labelOf(row.employmentType)} />
            <Fact label="Work mode" value={labelOf(row.workMode)} />
            <Fact label="Location" value={orEmpty(row.location)} />
            <Fact label="Hiring manager" value={orEmpty(row.hiringManagerName)} />
            <Fact label="Seats" value={`${row.openSlots} open of ${row.totalSlots}`} />
            <Fact
              label="Salary band"
              // §L23: restricted renders as an em dash here exactly as it does in
              // the list and the export, because all three read the same masked row.
              value={<SalaryBand position={row} />}
            />
          </dl>
        </Card>

        <Card title="Vacancy" meta="Computed from the event log, never stored">
          <dl className={styles.facts}>
            <Fact
              label="Vacant since"
              value={row.vacantSince === null ? EMPTY : formatDateTime(row.vacantSince)}
            />
            <Fact
              label="Days vacant"
              value={row.daysVacant === null ? EMPTY : formatNumber(row.daysVacant)}
            />
            {isArchived && (
              <Fact label="Archived" value={formatDateTime(row.archivedAt as string)} />
            )}
            {row.archiveNote !== null && <Fact label="Archive note" value={row.archiveNote} />}
          </dl>
        </Card>

        <Card
          title="Requisitions"
          meta={
            activeRequisition
              ? 'One active requisition — a second is refused'
              : 'Every request ever raised against this seat'
          }
        >
          {row.requisitions.length === 0 ? (
            <p className={styles.none}>Nothing has been raised against this seat yet.</p>
          ) : (
            <ul className={styles.requisitions}>
              {row.requisitions.map((requisition) => (
                <li key={requisition.id} className={styles.requisition}>
                  <span className={styles.requisitionRef}>{requisition.reference}</span>
                  <span className={styles.requisitionTitle}>{requisition.title}</span>
                  <Chip tone={requisitionTone(requisition)} plain>
                    {labelOf(requisition.status)}
                  </Chip>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="History" meta="Every change, in order">
        <PositionHistory entries={row.history} />
      </Card>

      {isFreezing && <FreezeDialog position={row} onClose={() => setIsFreezing(false)} />}
      {isReassigning && <ReassignDialog position={row} onClose={() => setIsReassigning(false)} />}
    </div>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: ReactNode }): ReactNode {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}

function requisitionTone(requisition: LinkedRequisition): Tone {
  if (isRequisitionStatus(requisition.status)) return REQUISITION_STATUS_TONES[requisition.status];
  return requisition.isActive ? 'progress' : 'ended';
}

function isRequisitionStatus(value: string): value is RequisitionStatus {
  return REQUISITION_STATUSES.some((status) => status === value);
}

/** `full_time` reads as "Full time" — sentence case, always (06-qol §11). */
function labelOf(value: string): string {
  const words = value.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
